import { useEffect, useRef, useState } from "react";
import { Loader2, Pencil, Plus, Star, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORIES, categoryName, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FIELD = "h-10 border-forest-700/20 bg-white";

const EMPTY = {
  name: "", brand: "", category: "heat-pumps", tagline: "", description: "",
  image: "", specs: [{ key: "", value: "" }], features: "", featured: false,
  est_savings: 0, sort_order: 0,
};

export default function AdminProducts() {
  const [products, setProducts] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/products").then((d) => setProducts(d.data)).catch((e) => {
    toast.error(formatApiErrorDetail(e.response?.data?.detail));
    setProducts([]);
  });

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name, brand: p.brand || "", category: p.category, tagline: p.tagline || "",
      description: p.description || "", image: p.image || "",
      specs: p.specs?.length ? p.specs.map((s) => ({ key: s.key, value: s.value })) : [{ key: "", value: "" }],
      features: (p.features || []).join("\n"),
      featured: !!p.featured, est_savings: p.est_savings || 0, sort_order: p.sort_order || 0,
    });
    setOpen(true);
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      set("image", data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name || !form.category) {
      toast.error("Name and category are required");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name, brand: form.brand, category: form.category, tagline: form.tagline,
      description: form.description, image: form.image,
      specs: form.specs.filter((s) => s.key || s.value),
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      featured: form.featured, est_savings: Number(form.est_savings) || 0, sort_order: Number(form.sort_order) || 0,
    };
    try {
      if (editId) await api.put(`/admin/products/${editId}`, payload);
      else await api.post("/admin/products", payload);
      setOpen(false);
      toast.success(editId ? "Product updated" : "Product added");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/products/${deleteTarget.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleteTarget(null);
    }
  };

  const filtered = (products || []).filter((p) => catFilter === "all" || p.category === catFilter);

  return (
    <div data-testid="admin-products">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="h-9 w-44 border-forest-700/20 bg-white" data-testid="product-filter-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs font-bold text-moss" data-testid="products-count">
          {products === null ? "Loading…" : `${filtered.length} product${filtered.length === 1 ? "" : "s"}`}
        </span>
        <Button onClick={openNew} data-testid="admin-add-product" className="ml-auto gap-2 rounded-full bg-terra hover:bg-terra-dark">
          <Plus className="h-4 w-4" /> Add product
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-forest-700/10 bg-white">
        <Table data-testid="products-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="w-16">Feat.</TableHead>
              <TableHead className="w-16">Order</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products === null ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-forest-700" />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-sm text-moss" data-testid="products-empty">
                  No products in this view yet — add your first one.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell>
                    {p.image ? (
                      <img src={p.image} alt="" className="h-10 w-10 rounded-md object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded-md bg-sage" />
                    )}
                  </TableCell>
                  <TableCell className="font-semibold text-forest-700">{p.name}</TableCell>
                  <TableCell className="text-moss">{p.brand || "—"}</TableCell>
                  <TableCell className="text-moss">{categoryName(p.category)}</TableCell>
                  <TableCell>{p.featured ? <Star className="h-4 w-4 fill-terra text-terra" /> : ""}</TableCell>
                  <TableCell className="text-moss">{p.sort_order}</TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      <Button variant="outline" size="icon" className="h-8 w-8 border-forest-700/20" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8 border-red-200 text-red-500" onClick={() => setDeleteTarget(p)} data-testid={`product-delete-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-forest-700">{editId ? "Edit product" : "Add product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Name *</Label>
                <Input className={FIELD} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-form-name" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Brand</Label>
                <Input className={FIELD} value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="product-form-brand" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Category *</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className={`${FIELD} w-full`} data-testid="product-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Sort order</Label>
                <Input type="number" className={FIELD} value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} data-testid="product-form-sort" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Est. savings ($/yr)</Label>
                <Input type="number" min="0" className={FIELD} value={form.est_savings} onChange={(e) => set("est_savings", e.target.value)} data-testid="product-form-est-savings" />
                <p className="mt-1 text-[11px] text-moss/70">Shown as "Save up to ~$X a year" on the product page. 0 hides the line.</p>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Tagline</Label>
              <Input className={FIELD} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="product-form-tagline" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="product-form-description" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Product image</Label>
              <div className="mt-2 flex items-center gap-4">
                {form.image ? (
                  <img src={form.image} alt="" className="h-16 w-16 rounded-md object-cover" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-md bg-sage text-xs text-moss">None</div>
                )}
                <input type="file" accept="image/*" ref={fileRef} onChange={onUpload} className="hidden" data-testid="product-form-image-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2 border-forest-700/20" data-testid="product-form-upload-button">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Uploading…" : "Upload image"}
                </Button>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Specs table</Label>
                <Button type="button" variant="ghost" size="sm" onClick={() => set("specs", [...form.specs, { key: "", value: "" }])} data-testid="product-form-add-spec">
                  <Plus className="h-3.5 w-3.5" /> Add row
                </Button>
              </div>
              <div className="space-y-2">
                {form.specs.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <Input placeholder="Spec (e.g. Tank capacity)" className={FIELD} value={s.key} onChange={(e) => set("specs", form.specs.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} data-testid={`product-form-spec-key-${i}`} />
                    <Input placeholder="Value (e.g. 270 litres)" className={FIELD} value={s.value} onChange={(e) => set("specs", form.specs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} data-testid={`product-form-spec-value-${i}`} />
                    <Button type="button" variant="ghost" size="icon" className="shrink-0 text-red-500" onClick={() => set("specs", form.specs.filter((_, j) => j !== i))} data-testid={`product-form-remove-spec-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Key features (one per line)</Label>
              <Textarea rows={3} value={form.features} onChange={(e) => set("features", e.target.value)} data-testid="product-form-features" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="product-form-featured" />
              <Label className="text-sm text-forest-700">Featured ("Popular" badge on site)</Label>
            </div>
            <div className="flex justify-end gap-3 border-t border-forest-700/10 pt-4">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="product-form-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} className="gap-2 rounded-full bg-forest-700 hover:bg-forest-600" data-testid="product-form-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editId ? "Save changes" : "Add product"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="product-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the product from the public site immediately. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="product-delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
